import { ElementCategory } from "@easy-email-pro-core/constants";
import {
  Element,
  Element as NodeElement,
  PageElement,
} from "@easy-email-pro-core/typings";
import { unescape } from "he";
import { BlockManager } from "../BlockManager";
import { JsonToMjmlOption, isProductionMode } from "../isProductionMode";
import {
  getUniversalElements,
  transformUniversalElements,
} from "./universalElement";
import { html } from "js-beautify";
import { NodeUtils } from "@easy-email-pro-core/utils/NodeUtils";
import { cloneDeep, forOwn, get, omit } from "lodash";
import {
  EmailGlobalContextProvider,
  GlobalEmailContext,
} from "./contexts/EmailGlobalContext";
import { renderToStaticMarkup } from "react-dom/server";
import { EmailRenderProvider } from "./contexts/EmailRenderContext";
import React from "react";
import { BlockRenderer } from "@easy-email-pro-core/components";
import { EditorAuth } from "../EditorAuth";
import { mjmlToBlockElement } from "./mjmlToBlockElement";
import objectHash from "object-hash";

export class EditorCore {
  static autoComplete = false;

  static setAutoComplete(autoComplete: boolean) {
    this.autoComplete = autoComplete;
  }

  static version = "0.14.0";

  static async auth(clientId: string) {
    await EditorAuth.auth(clientId);
  }

  static toMJML(options: JsonToMjmlOption) {
    const authState = EditorAuth.getAuthState();
    if (authState === "PENDING") {
      throw new Error("Please call EditorCore.auth first");
    }
    if (authState === "ERROR") {
      throw new Error("Auth error");
    }
    const {
      element,
      pageElement,
      beautify,
      universalElements,
      mode,
      displayMode = "desktop-mobile",
      idx,
      mergetagsData,
      attributesVariables,
    } = options;

    const block = BlockManager.getBlockByType(element.type);
    if (!block) {
      throw new Error(`Block ${element.type} not found`);
    }

    if (!pageElement && block.category !== ElementCategory.PAGE) {
      throw new Error("Page element is required if element is not a page");
    }

    const cloneElement = universalElements
      ? EditorCore.transformUniversalElements({
          universalElements: universalElements.elements,
          content: cloneDeep(element),
        })
      : cloneDeep(element);

    const renderContext = new GlobalEmailContext({ mode });
    const blockNodeContext = {
      content: pageElement || (element as PageElement),
    };
    let headerElementString = "";
    let footerElementString = "";
    let nodeIdx = idx;
    if (block.category === ElementCategory.PAGE) {
      nodeIdx = NodeUtils.getPageIdx();

      const frozenPageElement = cloneDeep(
        pageElement || (cloneElement as PageElement)
      );

      frozenPageElement.attributes = omit(
        frozenPageElement.attributes,
        "content-background-color"
      );

      if (options.headerElement || options.footerElement) {
        const isEnabled = EditorAuth.getFeatureEnabled("FROZEN_BLOCK");
        if (!isEnabled) {
          throw new Error(`Current plan do not support FROZEN_BLOCK`);
        }

        const frozenBlockNodeContext = {
          content: frozenPageElement,
        };

        if (options.headerElement) {
          headerElementString = unescape(
            renderToStaticMarkup(
              <EmailRenderProvider
                universalElements={universalElements}
                mode={options.mode}
                context={frozenBlockNodeContext}
                displayMode={displayMode}
                keepEmptyAttributes={Boolean(options.keepEmptyAttributes)}
                mergetagsData={mergetagsData}
                attributesVariables={attributesVariables}
              >
                <EmailGlobalContextProvider context={renderContext}>
                  <BlockRenderer
                    idx={nodeIdx}
                    node={cloneDeep(options.headerElement)}
                  />
                </EmailGlobalContextProvider>
              </EmailRenderProvider>
            )
          );
        }

        if (options.footerElement) {
          footerElementString = unescape(
            renderToStaticMarkup(
              <EmailRenderProvider
                universalElements={universalElements}
                mode={options.mode}
                context={frozenBlockNodeContext}
                displayMode={displayMode}
                keepEmptyAttributes={Boolean(options.keepEmptyAttributes)}
                mergetagsData={mergetagsData}
                attributesVariables={attributesVariables}
              >
                <EmailGlobalContextProvider context={renderContext}>
                  <BlockRenderer
                    idx={nodeIdx}
                    node={cloneDeep(options.footerElement)}
                  />
                </EmailGlobalContextProvider>
              </EmailRenderProvider>
            )
          );
        }
      }
    }

    let mjmlString = unescape(
      renderToStaticMarkup(
        <EmailRenderProvider
          universalElements={universalElements}
          mode={options.mode}
          context={blockNodeContext}
          displayMode={displayMode}
          keepEmptyAttributes={Boolean(options.keepEmptyAttributes)}
          mergetagsData={mergetagsData}
          attributesVariables={attributesVariables}
        >
          <EmailGlobalContextProvider context={renderContext}>
            <BlockRenderer idx={nodeIdx} node={cloneElement} />
          </EmailGlobalContextProvider>
        </EmailRenderProvider>
      )
    );

    const headerItemsText = renderContext.getHeadContent();

    mjmlString = mjmlString.replace(
      /\<!-- GLOBAL_EXTRACT_HEADER --\>/,
      headerItemsText
    );
    // https://cdn.shopify.com/s/files/1/0863/8971/9346/files/image.png
    if (!EditorAuth.getFeatureEnabled("REMOVE_BRAND")) {
      mjmlString = mjmlString.replace(
        /\<!-- EASY_EMAIL_BRAND --\>/,
        `<mj-section padding-top="20px" padding-bottom="0px">
       <mj-column width="150px">
         <mj-image target="_blank" alt="easy email pro" href="https://www.easyemail.pro/?utm_source=brand_logo&utm_client_id=${EditorAuth.clientId}" padding="5px 10px" container-background-color="#fafafa" src="https://cdn.shopify.com/s/files/1/0863/8971/9346/files/image.png" />
       </mj-column>
     </mj-section>`
      );
    } else {
      mjmlString = mjmlString.replace(/\<!-- EASY_EMAIL_BRAND --\>/g, "");
    }

    mjmlString = mjmlString.replace(
      /\<!-- GLOBAL_EXTRACT_HEADER --\>/,
      headerItemsText
    );
    mjmlString = mjmlString
      .replace(/\<!-- EASY_EMAIL_FROZEN_HEADER --\>/, headerElementString)
      .replace(/\<!-- EASY_EMAIL_FROZEN_FOOTER --\>/, footerElementString);

    if (!isProductionMode(options)) {
      mjmlString = mjmlString.replace(
        /\<mj-html-attributes\>([\w\W]*?)\<\/mj-html-attributes\>/,
        ""
      );
    }

    if (beautify) {
      return html(mjmlString, {
        indent_size: 2,
        wrap_attributes_indent_size: 2,
        max_preserve_newlines: 0,
        preserve_newlines: false,
      });
    }
    return mjmlString;
  }

  static async translate(options: {
    template: {
      subject: string;
      content: PageElement;
    };
    translate: (words: string[]) => Promise<Record<string, string>>;
    helper?: {
      extract: (element: Element, words: string[]) => Array<string>;
      replace: (element: Element, wordsMap: Record<string, string>) => void;
    };
  }) {
    const isEnabled = EditorAuth.getFeatureEnabled("TRANSLATE_TEMPLATE");
    if (!isEnabled) {
      throw new Error(`Current plan do not support translate template.`);
    }

    const words = [options.template.subject];

    const loopExtract = (element: Element) => {
      if (NodeUtils.isMergetagElement(element)) return;
      if (NodeUtils.isElement(element)) {
        element.children.forEach((child) => {
          if (NodeUtils.isTextNode(child)) {
            if (child.text.trim()) {
              words.push(child.text);
            }
          } else {
            loopExtract(child);
          }
        });
      }
      if (options.helper?.extract) {
        options.helper.extract(element, words);
      }
    };

    loopExtract(options.template.content);

    const newWordsMap = await options.translate([...new Set(words)]);

    const loopReplace = (element: Element) => {
      if (NodeUtils.isMergetagElement(element)) return;
      if (NodeUtils.isElement(element)) {
        element.children.forEach((child) => {
          if (NodeUtils.isTextNode(child)) {
            if (child.text.trim()) {
              child.text = newWordsMap[child.text];
            }
          } else {
            loopReplace(child);
          }
        });
      }
      if (options.helper?.replace) {
        options.helper.replace(element, newWordsMap);
      }
    };

    const newTemplateContent = cloneDeep(options.template.content);
    loopReplace(newTemplateContent);

    return {
      content: newTemplateContent,
      subject: newWordsMap[options.template.subject],
    };
  }

  static elementToMjml(
    node: NodeElement,
    options: Omit<JsonToMjmlOption, "element"> & { pageElement: NodeElement }
  ) {
    const renderContext = new GlobalEmailContext({ mode: options.mode });
    const mjmlStr = unescape(
      renderToStaticMarkup(
        <EmailRenderProvider
          universalElements={options.universalElements}
          mode={options.mode}
          context={{ content: options.pageElement }}
          displayMode={options.displayMode || "desktop-mobile"}
          keepEmptyAttributes={Boolean(options.keepEmptyAttributes)}
          mergetagsData={options.mergetagsData}
          attributesVariables={options.attributesVariables}
        >
          <EmailGlobalContextProvider context={renderContext}>
            <BlockRenderer node={cloneDeep(node)} />
          </EmailGlobalContextProvider>
        </EmailRenderProvider>
      )
    );
    return mjmlStr;
  }

  static elementComponentToMjml(
    content: React.ReactNode,
    options: Omit<JsonToMjmlOption, "element"> & { pageElement: NodeElement }
  ) {
    const renderContext = new GlobalEmailContext({ mode: options.mode });
    const mjmlStr = unescape(
      renderToStaticMarkup(
        <EmailRenderProvider
          universalElements={options.universalElements}
          mode={options.mode}
          context={{ content: options.pageElement }}
          displayMode={options.displayMode || "desktop-mobile"}
          keepEmptyAttributes={Boolean(options.keepEmptyAttributes)}
          mergetagsData={options.mergetagsData}
          attributesVariables={options.attributesVariables}
        >
          <EmailGlobalContextProvider context={renderContext}>
            {content}
          </EmailGlobalContextProvider>
        </EmailRenderProvider>
      )
    );
    return mjmlStr;
  }

  static mjmlToBlockElement = mjmlToBlockElement;

  static isPageDataVariable(text: string) {
    return /\$var\(([\s\S]+)\)/g.test(text);
  }

  static getPageVariableKey = (text: string) => {
    return text.replace(/\$var\(([\s\S]+)\)/g, "$1");
  };

  static getPageDataVariables = (
    pageVariables: PageElement["data"]["variables"],
    attributesVariables: Record<string, any>
  ): Record<string, any> => {
    return (
      pageVariables?.reduce(
        (prev, item) => {
          return {
            ...prev,
            [item.name]: item.value,
          };
        },
        { ...attributesVariables }
      ) || { ...attributesVariables }
    );
  };

  static renderWithPageVariables = <T extends Object | string>(
    obj: T,
    data?: Record<string, any>,
    includeChildren?: boolean
  ): T => {
    if (!data) return obj;
    const cloneObj = cloneDeep(obj) as any;
    if (typeof cloneObj === "string") {
      return cloneObj.replace(/\$var\(([^)]+)\)/g, (group, $1) => {
        return get(data, $1.trim(), "");
      }) as any;
    }

    const loopObject = (obj: any) => {
      forOwn(obj, (value, key) => {
        if (typeof value === "object") {
          if (key !== "children" || includeChildren) {
            loopObject(value);
          }
        } else {
          if (typeof value === "string") {
            obj[key] = value
              .replace(/\$var\(([^)]+)\)/g, (group, $1) => {
                return get(data, $1.trim(), "");
              })
              .replace(/\$input\(([^)]+)\)/g, (group, $1) => {
                if (
                  NodeUtils.isElement(cloneObj) &&
                  NodeUtils.isWidgetElement(cloneObj)
                ) {
                  return `widget_element_${objectHash(cloneObj.data.input)}.${$1.trim()}`;
                }
                return $1.trim();
              });
          }
        }
      });
    };

    loopObject(cloneObj);
    return cloneObj;
  };

  static getWidgetElementsInputData = (
    element: Element
  ): Record<string, any> => {
    const data: Record<string, any> = {};
    const loopElement = (element: Element) => {
      if (NodeUtils.isWidgetElement(element)) {
        const hash = "widget_element_" + objectHash(element.data.input);
        data[hash] = element.data.input;
      }

      if (element.children) {
        element.children.forEach((child) => {
          if (NodeUtils.isElement(child)) {
            loopElement(child);
          }
        });
      }
    };

    loopElement(element);
    return data;
  };

  static getUniversalElements = getUniversalElements;

  static transformUniversalElements = transformUniversalElements;
}
